/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Button, Popover, theme, ModalConfirm } from "@nordcloud/gnui";
import { GlobalSpinner } from "~/components";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { useRemoveOrgUnit } from "~/hooks/graphql";
import { showError, showSuccess } from "~/services/toast";
import { generateActionConfirmText } from "~/tools";

type Props = {
  name: string;
  nid: string;
  redirectUrl: string;
};

export function OrganizationUnitControls({ name, nid, redirectUrl }: Props) {
  const [isModalConfirmOpen, setIsModalConfirmOpen] = React.useState(false);
  const [removeOrgUnit, { loading }] = useRemoveOrgUnit();

  const navigate = useNavigate();

  const onRemoveHandler = async (id: string) => {
    setIsModalConfirmOpen(false);

    try {
      await removeOrgUnit({ id });
      showSuccess(SUCCESS_TEXT.orgUnitRemoved);
      navigate(redirectUrl);
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  return (
    <>
      <GlobalSpinner loading={loading} />
      <Popover
        alignRight
        trigger={<Button severity="low" icon="menu" data-testid="popover" />}
      >
        <Button
          iconRight
          severity="low"
          css={{ backgroundColor: theme.color.background.ui01 }}
          icon="trash"
          onClick={() => setIsModalConfirmOpen(true)}
        >
          Delete Organizational Unit
        </Button>
      </Popover>
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        actionLabel="Delete"
        contentLabel="Delete Organizational Unit"
        confirm={() => onRemoveHandler(nid)}
        onClose={() => setIsModalConfirmOpen(false)}
      >
        {generateActionConfirmText(name)("delete")()}
      </ModalConfirm>
    </>
  );
}
